import { SUP_CAMPO, SUP_PRE, SUP_VALIDACAO, UNIDADES } from "@caint/data";
import type { SupAmostraItem } from "@caint/shared";
import { useMemo, useState } from "react";
import { itemDoCatalogo } from "../lib/supervisao.ts";
import "./print.css";

/** Resultados a circular nos critérios de processo. */
const RES = ["C", "NC", "NA"] as const;
/** Maturidade (cega) a circular na re-performance por amostra. */
const NIVEIS = ["0", "1", "2", "3", "4", "NA"] as const;
const STATUS = ["Validar", "Revisar"] as const;
const PARECERES = ["Confiável", "Com ressalvas", "Não confiável"] as const;
const LEG_RES = "C Conforme · NC Não conforme · NA Não aplicável";

export interface GrupoCriterios {
  readonly chave: "pre" | "campo" | "vt";
  readonly titulo: string;
  readonly itens: readonly { id: string; criterio: string; crit: string; norma?: string }[];
}

/**
 * Seções de critérios de processo (fixas) da Supervisão AUDIN — função pura para
 * ser testável sem DOM. A amostra (Estrato A) é gerada à parte por avaliação.
 */
export function gruposCriteriosSupervisao(): GrupoCriterios[] {
  const norm = (c: { id: string; criterio: string; crit: string; norma?: string }) => ({
    id: c.id,
    criterio: c.criterio,
    crit: c.crit,
    norma: c.norma,
  });
  return [
    { chave: "pre", titulo: "Pré-visita — verificar antes da partida", itens: SUP_PRE.map(norm) },
    {
      chave: "campo",
      titulo: "Supervisão em campo — conduta dos avaliadores",
      itens: SUP_CAMPO.map(norm),
    },
    {
      chave: "vt",
      titulo: "Validação independente da auditoria (documental · in loco · critérios)",
      itens: SUP_VALIDACAO.map(norm),
    },
  ];
}

export interface SupervisaoImpressaoProps {
  /** Volta à tela anterior (mobile). Ausente no desktop (navegação por sidebar). */
  onBack?: () => void;
  /** Amostra cega do ciclo (do estado da supervisão) — itens a re-performar à mão. */
  amostra?: readonly SupAmostraItem[];
  /** Unidade do ciclo (pré-preenche o cabeçalho, se houver). */
  unidadeId?: string;
  /** Nome do supervisor (AUDIN) logado, pré-preenchido no cabeçalho. */
  supervisor?: string;
}

/** Cabeçalho com os dados da visita (pré-preenchido quando conhecido). */
function MetaVisita({ unidade, supervisor }: { unidade?: string; supervisor?: string }) {
  return (
    <div className="ps-meta">
      Unidade: {unidade ?? "__________________"} · Ciclo: __________ · Supervisor (AUDIN):{" "}
      {supervisor ?? "________________"} · Data: ___/___/____
    </div>
  );
}

/**
 * Contingência offline / via física: gera o **Checklist do Supervisor (AUDIN)**
 * em branco para imprimir (ou salvar em PDF) e preencher à mão — igual ao
 * checklist dos avaliadores. Lê os critérios embarcados (`@caint/data`), então
 * funciona 100% sem internet; a amostra cega (Estrato A) é a do ciclo atual.
 */
export function SupervisaoImpressao({
  onBack,
  amostra = [],
  unidadeId,
  supervisor,
}: SupervisaoImpressaoProps) {
  const [selecao, setSelecao] = useState<string>("tudo");
  const grupos = useMemo(() => gruposCriteriosSupervisao(), []);
  const unidadeNome = useMemo(
    () => (unidadeId ? UNIDADES.find((u) => u.id === unidadeId)?.nome : undefined),
    [unidadeId],
  );

  const mostra = (chave: string) => selecao === "tudo" || selecao === chave;

  return (
    <div className="dk-page">
      <div className="no-print imp-controls">
        {onBack && (
          <button type="button" className="m-btn" onClick={onBack}>
            Voltar
          </button>
        )}
        <label htmlFor="imp-sup-sec">Seções</label>
        <select
          id="imp-sup-sec"
          className="m-select"
          value={selecao}
          onChange={(e) => setSelecao(e.target.value)}
        >
          <option value="tudo">Tudo (processo + amostra + parecer)</option>
          <option value="pre">Pré-visita</option>
          <option value="campo">Supervisão em campo</option>
          <option value="vt">Validação independente da auditoria</option>
          <option value="amostra">Re-performance por amostra (Estrato A)</option>
        </select>
        <button type="button" className="m-btn primary" onClick={() => window.print()}>
          Imprimir
        </button>
        <span className="imp-hint">
          Checklist do Supervisor (AUDIN) em branco para preencher à mão (contingência offline). Imprima ou
          salve em PDF; depois transcreva no app.
        </span>
      </div>

      <div className="print-sheet">
        {grupos
          .filter((g) => mostra(g.chave))
          .map((g) => (
            <section className="print-setor" key={g.chave}>
              <div className="ps-title">{g.titulo}</div>
              <MetaVisita unidade={unidadeNome} supervisor={supervisor} />
              <div className="ps-legend">Resultado: {LEG_RES}</div>

              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="c-cod">Cód.</th>
                    <th className="c-item">Critério de verificação</th>
                    <th className="c-mat">Resultado</th>
                    <th className="c-evi">Registro / observação</th>
                  </tr>
                </thead>
                <tbody>
                  {g.itens.map((it) => (
                    <tr key={it.id}>
                      <td className="c-cod">{it.id}</td>
                      <td className="c-item">
                        <div className="it-pergunta">{it.criterio}</div>
                        <div className="it-sub">
                          Criticidade: {it.crit}
                          {it.norma ? ` · ${it.norma}` : ""}
                        </div>
                      </td>
                      <td className="c-mat">
                        {RES.map((r) => (
                          <span className="cb" key={r}>
                            {r}
                          </span>
                        ))}
                      </td>
                      <td className="c-evi" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}

        {mostra("amostra") && (
          <section className="print-setor">
            <div className="ps-title">Camada 2 — Re-performance CEGA por amostra (Estrato A)</div>
            <MetaVisita unidade={unidadeNome} supervisor={supervisor} />
            <div className="ps-legend">
              Circule a maturidade observada SEM olhar a nota do avaliador (0–4 ou NA). A corroboração é
              apurada no app, no merge.
            </div>

            {amostra.length === 0 ? (
              <p className="ps-foot">
                Amostra ainda indisponível — ela é montada após 28 itens avaliados no checklist do ciclo.
              </p>
            ) : (
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="c-cod">Cód.</th>
                    <th className="c-item">Item de verificação</th>
                    <th className="c-mat">Maturidade (cega)</th>
                    <th className="c-enc">Status</th>
                    <th className="c-evi">Evidência re-verificada</th>
                  </tr>
                </thead>
                <tbody>
                  {amostra.map((a) => (
                    <tr key={a.itemId}>
                      <td className="c-cod">{a.itemId}</td>
                      <td className="c-item">
                        <div className="it-pergunta">{itemDoCatalogo(a.itemId)?.pergunta}</div>
                        <div className="it-sub">Estrato: {a.estrato}</div>
                      </td>
                      <td className="c-mat">
                        {NIVEIS.map((n) => (
                          <span className="cb" key={n}>
                            {n}
                          </span>
                        ))}
                      </td>
                      <td className="c-enc">
                        {STATUS.map((e) => (
                          <span className="cb cb-wide" key={e}>
                            {e}
                          </span>
                        ))}
                      </td>
                      <td className="c-evi" />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="ps-foot">
              Parecer de confiabilidade (circule um):{" "}
              {PARECERES.map((p) => (
                <span className="cb cb-wide" key={p}>
                  {p}
                </span>
              ))}
            </div>
            <div className="ps-foot">
              Assinatura do Supervisor (AUDIN): ____________________________ Data: ___/___/____
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
